import React from "react";
import { clsx } from "clsx";

import { CONTENT_TYPE } from "~constants";
import { LiveBlogItem } from "~components/shared/liveblog/LiveBlogItem";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    DEFAULT_MEDIA_PRIORITY,
} from "~components/shared/media/";
import PhoenixRelatedItems from "~components/shared/phoenix-related-items/PhoenixRelatedItems";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import TickerPills from "~components/shared/ticker-pills/TickerPills";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import { isOpinionItem } from "~models/transforms/eyebrow";
import {
    type VariationComponent,
    getRelatedStories,
    isCuratedStory,
    getIsOpinionPageTheme,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentBasic.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentBasic.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [
    ZoneColumnCombo.leftContentWell,
    ZoneColumnCombo.gridRightRail,
    ZoneColumnCombo.fullWidthContentWell,
];
const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.media,
    desktopMaxWidth: 725,
    mobileMaxWidth: 715,
};

export function getMediaPriority(
    renderingLocation: ZoneColumnCombo
): MediaFormat[] {
    if (renderingLocation === ZoneColumnCombo.fullWidthContentWell) {
        return [
            MediaFormat.carousel,
            MediaFormat.verticalVideo,
            ...DEFAULT_MEDIA_PRIORITY,
        ];
    }

    return [MediaFormat.carousel, ...DEFAULT_MEDIA_PRIORITY];
}

const LineupContentBasic = (props: VariationComponent) => {
    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    const { items, renderingLocation, theme } = props;

    const story = items[0];
    const { type } = story;

    const isOpinionStory = isOpinionItem(story);
    const isLiveBlog = type === CONTENT_TYPE.liveblog;
    const isRightRail = renderingLocation === ZoneColumnCombo.gridRightRail;
    const gridClassName =
        isOpinionStory && !getIsOpinionPageTheme(theme)
            ? styles.withOpinionBackground
            : "";

    return (
        <VariantBaseWithHeader
            source={props}
            className={clsx(styles.LineupContentBasic, [
                styles[renderingLocation],
                getIsOpinionPageTheme(theme) && styles.isOpinionTheme,
            ])}
            gridClassName={gridClassName}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <div className={styles.container}>
                {isLiveBlog && !isRightRail && isCuratedStory(story) ? (
                    <LiveBlogItem item={story} enableMediaCarousel />
                ) : (
                    <>
                        <Media
                            source={story}
                            priority={getMediaPriority(renderingLocation)}
                            config={MEDIA_CONFIG}
                        />
                        <div>
                            <StoryBlock
                                storyIndex={1}
                                story={story}
                                withDek
                                isBylineEligible
                                isSecondaryStory={isRightRail}
                                className={styles.storyBlock}
                                withOptionalEyebrow={
                                    !getIsOpinionPageTheme(theme)
                                }
                            />
                            <TickerPills
                                storyIndex={1}
                                tickerIds={story.terminalTickers}
                            />
                            {!isRightRail && (
                                <PhoenixRelatedItems
                                    storyIndex={1}
                                    relatedItems={
                                        getRelatedStories(story) || []
                                    }
                                    relatedTitle={story.relatedTitle}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </VariantBaseWithHeader>
    );
};

export default LineupContentBasic;
